import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PdfImportModal from '../components/PdfImportModal';
import TagSelector from '../components/TagSelector';

const RecipeForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const isEditing = Boolean(id);
  const fileInputRef = useRef(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [ingredients, setIngredients] = useState([{ name: '', quantity: '' }]);
  const [steps, setSteps] = useState(['']);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Image state
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImage, setExistingImage] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);

  // Drag state
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedType, setDraggedType] = useState(null); // 'ingredient' or 'step'
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // PDF Import state
  const [showPdfImport, setShowPdfImport] = useState(false);

  // Handle PDF import
  const handlePdfImport = (importedData) => {
    if (importedData.name) setName(importedData.name);
    if (importedData.description) setDescription(importedData.description);
    if (importedData.ingredients && importedData.ingredients.length > 0) {
      setIngredients(importedData.ingredients);
    }
    if (importedData.steps && importedData.steps.length > 0) {
      setSteps(importedData.steps);
    }
  };

  useEffect(() => {
    if (isEditing) {
      fetchRecipe();
    }
  }, [id]);

  const fetchRecipe = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/recipes/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.error);

      setName(data.data.name);
      setDescription(data.data.description || '');
      setIngredients(data.data.ingredients.map(i => ({ name: i.name, quantity: i.quantity || '' })));
      setSteps(data.data.steps.map(s => s.instruction));
      setTags(data.data.tags || []);
      
      if (data.data.imageUrl) {
        setExistingImage(data.data.imageUrl);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // Drag and Drop Handlers
  // ============================================
  
  const handleDragStart = (e, index, type) => {
    setDraggedItem(index);
    setDraggedType(type);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    // Add visual feedback
    setTimeout(() => {
      e.target.style.opacity = '0.4';
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedItem(null);
    setDraggedType(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index, type) => {
    e.preventDefault();
    if (draggedType !== type) return;
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex, type) => {
    e.preventDefault();
    if (draggedType !== type || draggedItem === null) return;

    if (type === 'ingredient') {
      const newIngredients = [...ingredients];
      const [draggedIngredient] = newIngredients.splice(draggedItem, 1);
      newIngredients.splice(dropIndex, 0, draggedIngredient);
      setIngredients(newIngredients);
    } else if (type === 'step') {
      const newSteps = [...steps];
      const [draggedStep] = newSteps.splice(draggedItem, 1);
      newSteps.splice(dropIndex, 0, draggedStep);
      setSteps(newSteps);
    }

    setDraggedItem(null);
    setDraggedType(null);
    setDragOverIndex(null);
  };

  // ============================================
  // Image Handlers
  // ============================================

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setError('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
        return;
      }
      
      if (file.size > 10 * 1024 * 1024) {
        setError('Image must be smaller than 10MB');
        return;
      }

      setImageFile(file);
      setRemoveImage(false);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setRemoveImage(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // ============================================
  // Ingredient Handlers
  // ============================================

  const handleAddIngredient = () => {
    setIngredients([...ingredients, { name: '', quantity: '' }]);
  };

  const handleRemoveIngredient = (index) => {
    if (ingredients.length > 1) {
      setIngredients(ingredients.filter((_, i) => i !== index));
    }
  };

  const handleIngredientChange = (index, field, value) => {
    const updated = [...ingredients];
    updated[index][field] = value;
    setIngredients(updated);
  };

  // ============================================
  // Step Handlers
  // ============================================

  const handleAddStep = () => {
    setSteps([...steps, '']);
  };

  const handleRemoveStep = (index) => {
    if (steps.length > 1) {
      setSteps(steps.filter((_, i) => i !== index));
    }
  };

  const handleStepChange = (index, value) => {
    const updated = [...steps];
    updated[index] = value;
    setSteps(updated);
  };

  // ============================================
  // Form Submit
  // ============================================

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const url = isEditing ? `/api/recipes/${id}` : '/api/recipes';
      const method = isEditing ? 'PUT' : 'POST';

      const formData = new FormData();
      formData.append('name', name);
      formData.append('description', description);
      formData.append('ingredients', JSON.stringify(ingredients.filter(i => i.name.trim())));
      formData.append('steps', JSON.stringify(steps.filter(s => s.trim())));
      formData.append('tags', JSON.stringify(tags));
      
      if (imageFile) {
        formData.append('image', imageFile);
      }
      
      if (removeImage) {
        formData.append('removeImage', 'true');
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.error);

      navigate(isEditing ? `/recipes/${id}` : '/recipes');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const displayImage = imagePreview || (!removeImage && existingImage);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>{isEditing ? '✏️ Edit Recipe' : '➕ New Recipe'}</h1>
        {!isEditing && (
          <button 
            type="button" 
            onClick={() => setShowPdfImport(true)} 
            style={styles.pdfImportBtn}
          >
            📄 Import from PDF
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} style={styles.form}>
        {error && <div style={styles.error}>{error}</div>}

        {/* Image Upload Section */}
        <div style={styles.imageSection}>
          <label style={styles.label}>📷 Recipe Image (Optional)</label>
          
          {displayImage ? (
            <div style={styles.imagePreviewContainer}>
              <img 
                src={displayImage} 
                alt="Recipe preview" 
                style={styles.imagePreview}
              />
              <button 
                type="button" 
                onClick={handleRemoveImage}
                style={styles.removeImageBtn}
              >
                ✕ Remove Image
              </button>
            </div>
          ) : (
            <div 
              style={styles.uploadArea}
              onClick={() => fileInputRef.current?.click()}
            >
              <div style={styles.uploadIcon}>📷</div>
              <p style={styles.uploadText}>Click to upload an image</p>
              <p style={styles.uploadHint}>JPEG, PNG, GIF, or WebP (max 10MB)</p>
              <p style={styles.uploadHint}>Images auto-compressed to under 1MB</p>
            </div>
          )}
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            onChange={handleImageChange}
            style={styles.hiddenInput}
          />
          
          {displayImage && (
            <button 
              type="button"
              onClick={() => fileInputRef.current?.click()}
              style={styles.changeImageBtn}
            >
              📷 Change Image
            </button>
          )}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Recipe Name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={styles.input}
            placeholder="e.g., Pasta Carbonara"
            required
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={styles.textarea}
            placeholder="Brief description of the recipe..."
            rows={3}
          />
        </div>

        {/* Tags */}
        <div style={styles.field}>
          <label style={styles.label}>🏷️ Tags</label>
          <TagSelector selectedTags={tags} onChange={setTags} />
        </div>

        {/* Ingredients with Drag & Drop */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <label style={styles.label}>🥕 Ingredients *</label>
            <div style={styles.sectionActions}>
              <span style={styles.dragHint}>⇅ Drag to reorder</span>
              <button type="button" onClick={handleAddIngredient} style={styles.addBtn}>+ Add</button>
            </div>
          </div>
          
          {ingredients.map((ing, index) => (
            <div
              key={index}
              draggable
              onDragStart={(e) => handleDragStart(e, index, 'ingredient')}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, index, 'ingredient')}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index, 'ingredient')}
              style={{
                ...styles.draggableRow,
                ...(dragOverIndex === index && draggedType === 'ingredient' ? styles.dragOver : {}),
                ...(draggedItem === index && draggedType === 'ingredient' ? styles.dragging : {})
              }}
            >
              <div style={styles.dragHandle}>⋮⋮</div>
              <input
                type="text"
                value={ing.quantity}
                onChange={(e) => handleIngredientChange(index, 'quantity', e.target.value)}
                style={styles.quantityInput}
                placeholder="Qty"
              />
              <input
                type="text"
                value={ing.name}
                onChange={(e) => handleIngredientChange(index, 'name', e.target.value)}
                style={styles.ingredientInput}
                placeholder="Ingredient name"
              />
              <button 
                type="button" 
                onClick={() => handleRemoveIngredient(index)} 
                style={styles.removeBtn}
              >
                ×
              </button>
            </div>
          ))}
        </div>

        {/* Steps with Drag & Drop */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <label style={styles.label}>📝 Steps *</label>
            <div style={styles.sectionActions}>
              <span style={styles.dragHint}>⇅ Drag to reorder</span>
              <button type="button" onClick={handleAddStep} style={styles.addBtn}>+ Add</button>
            </div>
          </div>
          
          {steps.map((step, index) => (
            <div
              key={index}
              draggable
              onDragStart={(e) => handleDragStart(e, index, 'step')}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, index, 'step')}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index, 'step')}
              style={{
                ...styles.draggableRow,
                alignItems: 'flex-start',
                ...(dragOverIndex === index && draggedType === 'step' ? styles.dragOver : {}),
                ...(draggedItem === index && draggedType === 'step' ? styles.dragging : {})
              }}
            >
              <div style={{...styles.dragHandle, paddingTop: '0.5rem'}}>⋮⋮</div>
              <span style={styles.stepNumber}>{index + 1}.</span>
              <textarea
                value={step}
                onChange={(e) => handleStepChange(index, e.target.value)}
                style={styles.stepInput}
                placeholder="Describe this step..."
                rows={2}
              />
              <button 
                type="button" 
                onClick={() => handleRemoveStep(index)} 
                style={{...styles.removeBtn, marginTop: '0.25rem'}}
              >
                ×
              </button>
            </div>
          ))}
        </div>

        <div style={styles.actions}>
          <button type="button" onClick={() => navigate(-1)} style={styles.cancelBtn}>Cancel</button>
          <button type="submit" style={styles.submitBtn} disabled={loading}>
            {loading ? 'Saving...' : (isEditing ? 'Update Recipe' : 'Create Recipe')}
          </button>
        </div>
      </form>

      <PdfImportModal
        isOpen={showPdfImport}
        onClose={() => setShowPdfImport(false)}
        onImport={handlePdfImport}
      />
    </div>
  );
};

const styles = {
  container: { maxWidth: '700px', margin: '0 auto', padding: '1rem 2rem' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' },
  title: { fontSize: '1.8rem', color: '#1e3a5f', margin: 0 },
  pdfImportBtn: { backgroundColor: '#8b5cf6', color: 'white', border: 'none', padding: '0.6rem 1rem', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem' },
  form: { backgroundColor: 'white', borderRadius: '12px', padding: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' },
  error: { backgroundColor: '#fee2e2', color: '#dc2626', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem' },
  
  // Image upload styles
  imageSection: { marginBottom: '1.5rem' },
  imagePreviewContainer: { position: 'relative', marginTop: '0.5rem' },
  imagePreview: { 
    width: '100%', 
    maxHeight: '300px', 
    objectFit: 'cover', 
    borderRadius: '8px',
    border: '2px solid #e5e7eb'
  },
  removeImageBtn: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    backgroundColor: 'rgba(239, 68, 68, 0.9)',
    color: 'white',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '600'
  },
  uploadArea: {
    border: '2px dashed #d1d5db',
    borderRadius: '8px',
    padding: '2rem',
    textAlign: 'center',
    cursor: 'pointer',
    backgroundColor: '#f9fafb',
    transition: 'border-color 0.2s, background-color 0.2s',
    marginTop: '0.5rem'
  },
  uploadIcon: { fontSize: '3rem', marginBottom: '0.5rem' },
  uploadText: { color: '#374151', fontWeight: '500', margin: '0 0 0.25rem 0' },
  uploadHint: { color: '#9ca3af', fontSize: '0.85rem', margin: '0.25rem 0' },
  hiddenInput: { display: 'none' },
  changeImageBtn: {
    marginTop: '0.75rem',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: '1px solid #d1d5db',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '500',
    width: '100%'
  },
  
  field: { marginBottom: '1.5rem' },
  label: { display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' },
  input: { width: '100%', padding: '0.75rem', borderRadius: '8px', border: '2px solid #e5e7eb', fontSize: '1rem', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '0.75rem', borderRadius: '8px', border: '2px solid #e5e7eb', fontSize: '1rem', resize: 'vertical', boxSizing: 'border-box' },
  section: { marginBottom: '1.5rem' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' },
  sectionActions: { display: 'flex', alignItems: 'center', gap: '1rem' },
  dragHint: { fontSize: '0.75rem', color: '#9ca3af', fontStyle: 'italic' },
  addBtn: { backgroundColor: '#22c55e', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' },
  
  // Draggable row styles
  draggableRow: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '0.5rem',
    padding: '0.5rem',
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    cursor: 'grab',
    transition: 'all 0.2s ease',
    alignItems: 'center'
  },
  dragHandle: {
    color: '#9ca3af',
    cursor: 'grab',
    padding: '0 0.25rem',
    fontSize: '1rem',
    userSelect: 'none'
  },
  dragOver: {
    borderColor: '#3b82f6',
    borderStyle: 'dashed',
    backgroundColor: '#eff6ff'
  },
  dragging: {
    opacity: 0.5,
    backgroundColor: '#f3f4f6'
  },
  
  quantityInput: { width: '80px', padding: '0.5rem', borderRadius: '6px', border: '2px solid #e5e7eb' },
  ingredientInput: { flex: 1, padding: '0.5rem', borderRadius: '6px', border: '2px solid #e5e7eb' },
  removeBtn: { backgroundColor: '#ef4444', color: 'white', border: 'none', width: '32px', height: '32px', borderRadius: '6px', cursor: 'pointer', fontSize: '1.2rem', flexShrink: 0 },
  stepNumber: { fontWeight: '600', color: '#f97316', minWidth: '24px' },
  stepInput: { flex: 1, padding: '0.5rem', borderRadius: '6px', border: '2px solid #e5e7eb', resize: 'vertical' },
  actions: { display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '2rem' },
  cancelBtn: { backgroundColor: '#e5e7eb', color: '#374151', padding: '0.75rem 1.5rem', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600' },
  submitBtn: { backgroundColor: '#2563eb', color: 'white', padding: '0.75rem 1.5rem', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600' }
};

export default RecipeForm;
